// Policy (AIAgent_Policies) sub-entity client — restores the "Policies" Agent tab dropped in the
// 2026-08-19/20 AgentEditor.tsx refactor. Targets the real live backend
// (BizFirst.Ai.AiAgent.Api.Base's BasePolicyController), verified by reading the controller and
// entity directly.
//
// Route confirmed as briefed: `[Route("api/v1/ai-agent/policies")]` on BasePolicyController.cs —
// `/policies`, not `/agent-policies`.
//
// Fields — briefed shape partially confirmed, partially incomplete. Confirmed: no
// PolicyContent/IsActive on the entity; a required ResourceTypeID FK exists; six required
// AppliedOnAttribute1-6 string(300) columns exist. NOT mentioned by the briefing but real and
// required on Policy.cs: PolicyTypeID (int FK), PolicyName (string, 300), and PolicyDescription
// (string, 2000) — all three are `[Required]` on the entity, so this client's create/update
// payloads must always include them. Documented in DevelopmentHistoryLog.md.
//
// getPoliciesByAgent posts to `by-agent` (confirmed on BasePolicyController.cs), same
// `{ AgentID: { ID } }` shape as every other by-agent query in this package.

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type { AgentPolicyDTO, AgentPolicyFormValues } from '../types';

export class AgentPolicyAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  async getPoliciesByAgent(agentId: number): Promise<AdminApiResult<AgentPolicyDTO[]>> {
    const result = await this.postQuery<AgentPolicyDTO[]>('/api/v1/ai-agent/policies/by-agent', {
      AgentID: { ID: agentId },
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async createPolicy(agentId: number, policy: AgentPolicyFormValues): Promise<AdminApiResult<AgentPolicyDTO>> {
    return this.post<AgentPolicyDTO>('/api/v1/ai-agent/policies', { AgentID: agentId, ...policy });
  }

  async updatePolicy(agentPolicyId: number, policy: Partial<AgentPolicyFormValues>): Promise<AdminApiResult<AgentPolicyDTO>> {
    return this.put<AgentPolicyDTO>(`/api/v1/ai-agent/policies/${agentPolicyId}`, {
      AgentPolicyID: agentPolicyId,
      ...policy,
    });
  }

  async deletePolicy(agentPolicyId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-agent/policies/${agentPolicyId}`, { id: agentPolicyId });
  }
}
